// Knowledge Base — in-memory demo fixtures (Phase 1 read path).
// Spec: 20260706-portal-knowledge-base (§4 data model, §5 generation engine).
//
// These fixtures let the KB UI render, search and navigate end-to-end BEFORE
// the CARO-Api KB endpoints (T-P1-09..13) exist. They are a faithful superset
// of what the real read API returns: full per-locale content rows so the
// portal's own locale-resolution + `en` fallback logic runs for real.
//
// Every shape here conforms to the `@caro/shared` KB types (KbPage,
// KbPageContent, KbSection, KbSectionContent, KbGenerationRun). Screenshots are
// inline SVG data-URIs so nothing depends on a live file server.
//
// When the real API ships, `knowledgeBaseApi` flips off the mock (see
// apiClient.js `USE_KB_MOCKS`) and none of this ships to users.

// ── Placeholder screenshot generator ────────────────────────────────────────
// A deterministic labelled SVG rendered as a data-URI, standing in for a real
// captured screenshot. Kept small so the search-index / render paths are real
// without any binary asset dependency.
const shot = (label, tone = '#9A3F69') => {
  const safe = String(label).replace(/[<>&]/g, '');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360">
  <rect width="640" height="360" fill="#f4eef1"/>
  <rect x="0" y="0" width="640" height="48" fill="${tone}"/>
  <circle cx="28" cy="24" r="9" fill="#ffffff" opacity="0.85"/>
  <rect x="48" y="18" width="160" height="12" rx="6" fill="#ffffff" opacity="0.7"/>
  <rect x="24" y="80" width="592" height="230" rx="10" fill="#ffffff" stroke="#e0d2da"/>
  <rect x="48" y="112" width="260" height="18" rx="9" fill="${tone}" opacity="0.25"/>
  <rect x="48" y="150" width="420" height="12" rx="6" fill="#cbb9c4"/>
  <rect x="48" y="176" width="360" height="12" rx="6" fill="#cbb9c4"/>
  <rect x="440" y="250" width="150" height="40" rx="8" fill="${tone}"/>
  <text x="515" y="275" font-family="system-ui,Arial" font-size="15" fill="#ffffff" text-anchor="middle">${safe}</text>
  <text x="320" y="205" font-family="system-ui,Arial" font-size="13" fill="#8a7482" text-anchor="middle">demo screenshot · synthetic data</text>
</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

// ── Page fixtures (mix of fresh / stale / manual, en·fr·nl) ──────────────────

export const KB_PAGES_FIXTURE = [
  // 1) Screen page — auto, fresh. Links to the create-ticket action page.
  {
    id: 1,
    uuid: 'kb-page-0001',
    slug: 'tickets',
    pageKey: 'tickets',
    source: 'auto',
    kind: 'screen',
    title: 'Tickets',
    routePath: '/tickets',
    locked: false,
    stale: false,
    lastGeneratedAt: '2026-07-05T22:14:00.000Z',
    lastGeneratedRun: 42,
    content: [
      {
        id: 11,
        pageId: 1,
        locale: 'en',
        title: 'Tickets',
        introMd:
          'The **Tickets** screen is where support requests for a location or device are tracked from report to resolution. Use the toolbar to create, filter and search tickets.',
      },
      {
        id: 12,
        pageId: 1,
        locale: 'fr',
        title: 'Tickets',
        introMd:
          "L'écran **Tickets** permet de suivre les demandes d'assistance d'un site ou d'un appareil, du signalement à la résolution. Utilisez la barre d'outils pour créer, filtrer et rechercher des tickets.",
      },
      {
        id: 13,
        pageId: 1,
        locale: 'nl',
        title: 'Tickets',
        introMd:
          'Het scherm **Tickets** volgt supportaanvragen voor een locatie of toestel, van melding tot oplossing. Gebruik de werkbalk om tickets aan te maken, te filteren en te zoeken.',
      },
    ],
    sections: [
      {
        id: 101,
        pageId: 1,
        kbElementId: 'tickets.btn.create',
        elementKind: 'button',
        locked: false,
        stale: false,
        linkedPageId: 2,
        // The read API resolves the linked action page's slug for the section
        // link (§5.3). Carried alongside linkedPageId for the render.
        linkedPageSlug: 'tickets-create',
        screenshotUrl: shot('New ticket'),
        confidence: 'decorated',
        sortOrder: 0,
        content: [
          {
            id: 1011,
            sectionId: 101,
            locale: 'en',
            heading: 'New ticket',
            baseMd:
              '**What this does:** opens the ticket creation dialog.\n\n**Steps:**\n1. Click **New ticket**.\n2. Pick the location and device.\n3. Describe the problem and submit.\n\n**Result:** a ticket is created and appears at the top of the list. See the full walkthrough on the linked action page.',
          },
          {
            id: 1012,
            sectionId: 101,
            locale: 'fr',
            heading: 'Nouveau ticket',
            baseMd:
              "**Ce que fait ce bouton :** ouvre la fenêtre de création de ticket.\n\n**Étapes :**\n1. Cliquez sur **Nouveau ticket**.\n2. Choisissez le site et l'appareil.\n3. Décrivez le problème et validez.\n\n**Résultat :** un ticket est créé et apparaît en haut de la liste.",
          },
          {
            id: 1013,
            sectionId: 101,
            locale: 'nl',
            heading: 'Nieuw ticket',
            baseMd:
              '**Wat dit doet:** opent het dialoogvenster om een ticket aan te maken.\n\n**Stappen:**\n1. Klik op **Nieuw ticket**.\n2. Kies de locatie en het toestel.\n3. Beschrijf het probleem en verstuur.\n\n**Resultaat:** een ticket wordt aangemaakt en verschijnt bovenaan de lijst.',
          },
        ],
      },
      {
        id: 102,
        pageId: 1,
        kbElementId: 'tickets.filter.status',
        elementKind: 'field',
        locked: false,
        stale: false,
        screenshotUrl: shot('Status filter'),
        confidence: 'decorated',
        sortOrder: 1,
        content: [
          {
            id: 1021,
            sectionId: 102,
            locale: 'en',
            heading: 'Status filter',
            baseMd:
              '**What this does:** narrows the list to tickets in a chosen state (Open, In progress, Waiting, Solved).\n\n**When to use it:** to focus on the tickets that need your attention right now.',
          },
          {
            id: 1022,
            sectionId: 102,
            locale: 'fr',
            heading: 'Filtre de statut',
            baseMd:
              '**Ce que fait ce filtre :** restreint la liste aux tickets dans un état choisi (Ouvert, En cours, En attente, Résolu).\n\n**Quand l’utiliser :** pour vous concentrer sur les tickets qui demandent votre attention.',
          },
          {
            id: 1023,
            sectionId: 102,
            locale: 'nl',
            heading: 'Statusfilter',
            baseMd:
              '**Wat dit doet:** beperkt de lijst tot tickets in een gekozen status (Open, In behandeling, Wachtend, Opgelost).\n\n**Wanneer gebruiken:** om te focussen op de tickets die nu je aandacht nodig hebben.',
          },
        ],
      },
    ],
  },

  // 2) Action page — auto, fresh. CRUD-lifecycle sections (create/update/delete).
  {
    id: 2,
    uuid: 'kb-page-0002',
    slug: 'tickets-create',
    pageKey: 'tickets.action.create',
    source: 'auto',
    kind: 'action',
    title: 'Create a ticket',
    routePath: '/tickets',
    locked: false,
    stale: false,
    lastGeneratedAt: '2026-07-05T22:15:00.000Z',
    lastGeneratedRun: 42,
    content: [
      {
        id: 21,
        pageId: 2,
        locale: 'en',
        title: 'Create a ticket',
        introMd:
          'This manual walks the full lifecycle of a ticket — creating it, updating it, then closing it — exactly as the assistant performed it against the demo tenant.',
      },
      {
        id: 22,
        pageId: 2,
        locale: 'fr',
        title: 'Créer un ticket',
        introMd:
          "Ce manuel parcourt le cycle de vie complet d'un ticket — création, mise à jour, puis clôture.",
      },
      {
        id: 23,
        pageId: 2,
        locale: 'nl',
        title: 'Een ticket aanmaken',
        introMd:
          'Deze handleiding doorloopt de volledige levenscyclus van een ticket — aanmaken, bijwerken en afsluiten.',
      },
    ],
    sections: [
      {
        id: 201,
        pageId: 2,
        kbElementId: 'tickets.action.create.step',
        elementKind: 'action',
        lifecyclePhase: 'create',
        locked: false,
        stale: false,
        screenshotUrl: shot('Create'),
        confidence: 'decorated',
        sortOrder: 0,
        content: [
          {
            id: 2011,
            sectionId: 201,
            locale: 'en',
            heading: 'Create',
            baseMd:
              '**Steps:**\n1. Click **New ticket**.\n2. Choose a location and device.\n3. Write a short title and description.\n4. Click **Create**.\n\n**Result:** the ticket opens in the *Open* state.',
          },
          {
            id: 2012,
            sectionId: 201,
            locale: 'fr',
            heading: 'Créer',
            baseMd:
              '**Étapes :**\n1. Cliquez sur **Nouveau ticket**.\n2. Choisissez un site et un appareil.\n3. Saisissez un titre et une description.\n4. Cliquez sur **Créer**.\n\n**Résultat :** le ticket s’ouvre à l’état *Ouvert*.',
          },
          {
            id: 2013,
            sectionId: 201,
            locale: 'nl',
            heading: 'Aanmaken',
            baseMd:
              '**Stappen:**\n1. Klik op **Nieuw ticket**.\n2. Kies een locatie en toestel.\n3. Geef een titel en beschrijving.\n4. Klik op **Aanmaken**.\n\n**Resultaat:** het ticket staat op *Open*.',
          },
        ],
      },
      {
        id: 202,
        pageId: 2,
        kbElementId: 'tickets.action.update.step',
        elementKind: 'action',
        lifecyclePhase: 'update',
        locked: false,
        stale: false,
        screenshotUrl: shot('Update'),
        confidence: 'decorated',
        sortOrder: 1,
        content: [
          {
            id: 2021,
            sectionId: 202,
            locale: 'en',
            heading: 'Update',
            baseMd:
              '**Steps:**\n1. Open the ticket.\n2. Change the status or add a comment.\n3. Save.\n\n**Result:** the change is recorded in the ticket history.',
          },
          {
            id: 2022,
            sectionId: 202,
            locale: 'fr',
            heading: 'Mettre à jour',
            baseMd:
              '**Étapes :**\n1. Ouvrez le ticket.\n2. Changez le statut ou ajoutez un commentaire.\n3. Enregistrez.',
          },
          {
            id: 2023,
            sectionId: 202,
            locale: 'nl',
            heading: 'Bijwerken',
            baseMd:
              '**Stappen:**\n1. Open het ticket.\n2. Wijzig de status of voeg een opmerking toe.\n3. Bewaar.',
          },
        ],
      },
      {
        id: 203,
        pageId: 2,
        kbElementId: 'tickets.action.delete.step',
        elementKind: 'action',
        lifecyclePhase: 'delete',
        locked: false,
        stale: false,
        screenshotUrl: shot('Close', '#b23a48'),
        confidence: 'decorated',
        sortOrder: 2,
        content: [
          {
            id: 2031,
            sectionId: 203,
            locale: 'en',
            heading: 'Close / delete',
            baseMd:
              '**Steps:**\n1. Open the ticket.\n2. Click **Mark as solved** and confirm.\n\n**Result:** the ticket moves to *Solved* and drops out of the active queue.',
          },
          {
            id: 2032,
            sectionId: 203,
            locale: 'fr',
            heading: 'Clôturer',
            baseMd:
              '**Étapes :**\n1. Ouvrez le ticket.\n2. Cliquez sur **Marquer comme résolu** et confirmez.',
          },
          {
            id: 2033,
            sectionId: 203,
            locale: 'nl',
            heading: 'Afsluiten',
            baseMd:
              '**Stappen:**\n1. Open het ticket.\n2. Klik op **Markeer als opgelost** en bevestig.',
          },
        ],
      },
    ],
  },

  // 3) Screen page — auto, STALE with a LOCKED admin override on one section.
  {
    id: 3,
    uuid: 'kb-page-0003',
    slug: 'alarms',
    pageKey: 'alarms',
    source: 'auto',
    kind: 'screen',
    title: 'Alarms',
    routePath: '/alarms',
    locked: false,
    stale: true,
    lastGeneratedAt: '2026-07-04T09:02:00.000Z',
    lastGeneratedRun: 41,
    content: [
      {
        id: 31,
        pageId: 3,
        locale: 'en',
        title: 'Alarms',
        introMd:
          'The **Alarms** dashboard shows live and historical alarm events. Operators acknowledge, escalate and resolve alarms here.',
      },
      {
        id: 32,
        pageId: 3,
        locale: 'fr',
        title: 'Alarmes',
        introMd:
          "Le tableau de bord **Alarmes** affiche les événements d'alarme en direct et historiques.",
      },
      {
        id: 33,
        pageId: 3,
        locale: 'nl',
        title: 'Alarmen',
        introMd:
          'Het **Alarmen**-dashboard toont live en historische alarmmeldingen.',
      },
    ],
    sections: [
      {
        id: 301,
        pageId: 3,
        kbElementId: 'alarms.btn.acknowledge',
        elementKind: 'button',
        // Admin override + lock; regeneration flagged it stale (UI changed under it).
        locked: true,
        stale: true,
        screenshotUrl: shot('Acknowledge'),
        confidence: 'decorated',
        sortOrder: 0,
        content: [
          {
            id: 3011,
            sectionId: 301,
            locale: 'en',
            heading: 'Acknowledge',
            baseMd:
              '**What this does:** acknowledges the selected alarm so colleagues know it is being handled.',
            // Admin-authored override wins over base_md (effective = override ?? base).
            overrideMd:
              '**Acknowledge (team note):** press this within 2 minutes of an alarm. Acknowledging pauses the escalation timer and pages the on-call nurse. *This edit is locked and survives regeneration.*',
          },
          {
            id: 3012,
            sectionId: 301,
            locale: 'fr',
            heading: 'Acquitter',
            baseMd:
              "**Ce que fait ce bouton :** acquitte l'alarme sélectionnée.",
          },
          {
            id: 3013,
            sectionId: 301,
            locale: 'nl',
            heading: 'Bevestigen',
            baseMd:
              '**Wat dit doet:** bevestigt het geselecteerde alarm.',
          },
        ],
      },
      {
        id: 302,
        pageId: 3,
        // Undecorated element captured heuristically — lower confidence.
        kbElementId: 'alarms.heuristic.export',
        elementKind: 'button',
        locked: false,
        stale: false,
        screenshotUrl: shot('Export'),
        confidence: 'heuristic',
        sortOrder: 1,
        content: [
          {
            id: 3021,
            sectionId: 302,
            locale: 'en',
            heading: 'Export (detected)',
            baseMd:
              '**What this does (best guess):** exports the current alarm list. This control was detected heuristically and has not yet been decorated with a `data-kb-id`.',
          },
        ],
      },
    ],
  },

  // 4) Manual page — hand-authored, source='manual'. Never touched by the worker.
  {
    id: 4,
    uuid: 'kb-page-0004',
    slug: 'getting-started',
    pageKey: null,
    source: 'manual',
    kind: 'screen',
    title: 'Getting started',
    routePath: null,
    locked: false,
    stale: false,
    lastGeneratedAt: null,
    content: [
      {
        id: 41,
        pageId: 4,
        locale: 'en',
        title: 'Getting started with the CARO portal',
        introMd:
          '# Getting started\n\nWelcome to the CARO portal. This hand-written guide gets new staff productive fast.\n\n## First steps\n1. Sign in with your Microsoft account.\n2. Pick your **site** from the switcher in the top bar.\n3. Open **Tickets** to see open work.\n\n## Tips\n- Use the search box above to jump to any manual page.\n- Your language can be changed in **Settings → Language & Region**.',
      },
      {
        id: 42,
        pageId: 4,
        locale: 'fr',
        title: 'Prise en main du portail CARO',
        introMd:
          '# Prise en main\n\nBienvenue sur le portail CARO. Ce guide rédigé à la main aide les nouveaux collaborateurs à démarrer.\n\n## Premières étapes\n1. Connectez-vous avec votre compte Microsoft.\n2. Choisissez votre **site** dans le sélecteur en haut.\n3. Ouvrez **Tickets** pour voir le travail en cours.',
      },
      {
        id: 43,
        pageId: 4,
        locale: 'nl',
        title: 'Aan de slag met het CARO-portaal',
        introMd:
          '# Aan de slag\n\nWelkom bij het CARO-portaal. Deze handgeschreven gids helpt nieuwe medewerkers snel op weg.\n\n## Eerste stappen\n1. Meld je aan met je Microsoft-account.\n2. Kies je **site** in de schakelaar bovenaan.\n3. Open **Tickets** om openstaand werk te zien.',
      },
    ],
    sections: [],
  },

  // 5) Screen page — auto, fresh. English-only body (proves de/avl/wvl fallback note).
  {
    id: 5,
    uuid: 'kb-page-0005',
    slug: 'camera-requests',
    pageKey: 'camera-requests',
    source: 'auto',
    kind: 'screen',
    title: 'Camera requests',
    routePath: '/camera-requests',
    locked: false,
    stale: false,
    lastGeneratedAt: '2026-07-05T22:16:00.000Z',
    lastGeneratedRun: 42,
    content: [
      {
        id: 51,
        pageId: 5,
        locale: 'en',
        title: 'Camera requests',
        introMd:
          'Request and review camera footage under a two-step approval (site manager → data protection officer). Only approved footage is disclosed.',
      },
    ],
    sections: [
      {
        id: 501,
        pageId: 5,
        kbElementId: 'camera-requests.btn.new',
        elementKind: 'button',
        locked: false,
        stale: false,
        screenshotUrl: shot('New request'),
        confidence: 'decorated',
        sortOrder: 0,
        content: [
          {
            id: 5011,
            sectionId: 501,
            locale: 'en',
            heading: 'New request',
            baseMd:
              '**What this does:** starts a new camera-footage request. You choose the camera, the time window and the reason; the request then goes for approval.',
          },
        ],
      },
    ],
  },
];

// ── Generation-run fixture (status data, T-P0-01 KbGenerationRun) ────────────

export const KB_RUN_FIXTURE = {
  id: 42,
  uuid: 'kb-run-0042',
  trigger: 'deploy',
  githubRunId: 987654321,
  status: 'succeeded',
  phase: 'index',
  progressDone: 5,
  progressTotal: 5,
  pagesGenerated: 4,
  pagesSkipped: 1,
  pagesStaleFlag: 1,
  startedAt: '2026-07-05T22:12:00.000Z',
  finishedAt: '2026-07-05T22:16:30.000Z',
  errorSummary: null,
  errors: [],
};

export const KB_RUNS_FIXTURE = [
  KB_RUN_FIXTURE,
  {
    id: 41,
    uuid: 'kb-run-0041',
    trigger: 'manual',
    githubRunId: 987654100,
    status: 'partial',
    phase: 'publish',
    progressDone: 4,
    progressTotal: 5,
    pagesGenerated: 3,
    pagesSkipped: 1,
    pagesStaleFlag: 0,
    startedAt: '2026-07-04T09:00:00.000Z',
    finishedAt: '2026-07-04T09:04:10.000Z',
    errorSummary: '1 page failed to caption',
    errors: [
      {
        id: 9001,
        runId: 41,
        pageKey: 'alarms',
        routePath: '/alarms',
        phase: 'caption',
        message: 'LLM timeout while captioning alarms.heuristic.export',
      },
    ],
  },
];

// ── Append-only revision store (Phase 2 — LD-REVISIONS, §4.3) ────────────────
// Keyed by pageId → ordered list of KbPageRevision rows. The mock write ops
// append here on every save and never mutate/delete existing rows, mirroring
// the server-side append-only trigger. Seeded with a little history so the
// admin console's revision list + rollback are exercisable offline.

export const KB_REVISIONS_FIXTURE = {
  1: [
    {
      id: 1001,
      pageId: 1,
      revisionNo: 1,
      origin: 'worker',
      snapshotMd:
        '# Tickets\n\nThe Tickets screen tracks support requests from report to resolution.',
      summary: 'auto-regenerated',
      authorId: null,
      runId: 41,
      createdAt: '2026-07-04T09:03:00.000Z',
    },
    {
      id: 1002,
      pageId: 1,
      revisionNo: 2,
      origin: 'worker',
      snapshotMd:
        '# Tickets\n\nThe **Tickets** screen is where support requests for a location or device are tracked from report to resolution.',
      summary: 'auto-regenerated',
      authorId: null,
      runId: 42,
      createdAt: '2026-07-05T22:14:00.000Z',
    },
  ],
  3: [
    {
      id: 3001,
      pageId: 3,
      revisionNo: 1,
      origin: 'worker',
      snapshotMd: '# Alarms\n\nThe Alarms dashboard shows live and historical alarm events.',
      summary: 'auto-regenerated',
      authorId: null,
      runId: 41,
      createdAt: '2026-07-04T09:02:00.000Z',
    },
    {
      id: 3002,
      pageId: 3,
      revisionNo: 2,
      origin: 'admin',
      snapshotMd:
        '**Acknowledge (team note):** press this within 2 minutes of an alarm. Acknowledging pauses the escalation timer and pages the on-call nurse.',
      summary: 'admin edit — acknowledge section override',
      authorId: 7,
      runId: null,
      createdAt: '2026-07-04T14:20:00.000Z',
    },
  ],
  4: [
    {
      id: 4001,
      pageId: 4,
      revisionNo: 1,
      origin: 'admin',
      snapshotMd: '# Getting started\n\nWelcome to the CARO portal.',
      summary: 'admin edit — initial authoring',
      authorId: 7,
      runId: null,
      createdAt: '2026-07-03T10:00:00.000Z',
    },
  ],
};

// Monotonic id/revision helpers so mutations stay unique & append-only.
let _assetSeq = 9000;
let _pageSeq = 100;
let _sectionSeq = 9000;
let _contentSeq = 90000;
let _revisionSeq = 50000;

export const kbNextAssetId = () => ++_assetSeq;
export const kbNextPageId = () => ++_pageSeq;
export const kbNextSectionId = () => ++_sectionSeq;
export const kbNextContentId = () => ++_contentSeq;
export const kbNextRevisionId = () => ++_revisionSeq;
